<template>
	<div
		v-if="questsEnabled"
		class="quests-view"
		:class="{
			'quests-view--chronicle-popup-open': isChroniclePopupOpen,
			'quests-view--story-playing': isStoryPlaying,
			'quests-view--quest-popup-open': isQuestPopupOpen,
		}"
	>
		<ModalWindow
			v-if="shouldRenderQuestSubViews"
			:class="{
				'quests-view__main-modal': paramTabIndex === 0,
				'quests-view__main-modal--active':
					paramTabIndex === 0 && activeTrackedQuest,
				'quests-view__side-modal': paramTabIndex === 1,
			}"
			:heading="translate('ui.quests')"
			:isElevated="false"
			fullWidthContent
		>
			<template #content>
				<Tabs
					:tabs="settlementTabs"
					:selected-index="paramTabIndex"
					class="quests-view__tabs"
				>
					<template v-slot:tab1>
						<MainQuestsView
							@popup="isChroniclePopupOpen = QuestTypes.MAIN"
							@claim-rewards="claimRewards"
						/>
					</template>
					<template v-slot:listSlot1>
						<AppNotification
							v-if="numberOfClaimableMainQuests > 0"
							class="navigation__item__notification"
							:value="numberOfClaimableMainQuests"
							:type="StatusTypes.WARNING"
						/>
					</template>
					<template v-slot:tab2>
						<SideQuestsView
							@claim-rewards="claimRewards"
							@popup="isChroniclePopupOpen = QuestTypes.SIDE"
						/>
					</template>
					<template v-slot:listSlot2>
						<AppNotification
							v-if="numberOfClaimableSideQuests > 0"
							class="navigation__item__notification"
							:value="numberOfClaimableSideQuests"
							:type="StatusTypes.WARNING"
						/>
					</template>
					<template v-if="areDailyQuestsAvailable" v-slot:tab3>
						<DailyQuestsView @claim-rewards="claimRewards" />
					</template>
					<template v-if="areDailyQuestsAvailable" v-slot:listSlot3>
						<AppNotification
							v-if="numberOfClaimableDailyQuests > 0"
							class="navigation__item__notification"
							:value="numberOfClaimableDailyQuests"
							:type="StatusTypes.WARNING"
						/>
					</template>
				</Tabs>
			</template>
		</ModalWindow>
		<QuestChronicleStory v-if="isStoryPlaying" />
	</div>
</template>

<script lang="ts" setup>
import AppNotification from "@/components/AppNotification.vue";
import ModalWindow from "@/components/ModalWindow.vue";
import QuestPopup from "@/components/Quests/QuestPopup.vue";
import Tabs from "@/components/Tabs.vue";
import {
	QuestMetaActions,
	questMetaActionsConfig,
} from "@/game/entities/config/quests/quest_meta_actions";
import { QuestTypes } from "@/game/entities/config/quests/quest_types";
import {
	quests,
	quests as questsConfig,
} from "@/game/entities/config/quests/quests";
import {
	areStandardQuestsEnabled,
	isQuestAvailabilityReady,
	isQuestTypeEnabled,
} from "@/game/questAvailability";
import QuestRepository from "@/game/QuestRepository";
import SoundService from "@/game/SoundService";
import router from "@/router";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useQuestStore from "@/store/useQuestStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import type { QuestActive } from "@/types/quest";
import { QuestPopupType } from "@/types/quest";
import { RouteNames, SkinTypes, StatusTypes } from "@/types/ui";
import { getAmount } from "@/utils/number";
import { translate } from "@/utils/translate";
import QuestChroniclePopup from "@/views/Quests/QuestChroniclePopup.vue";
import QuestChronicleStory from "@/views/Quests/QuestChronicleStory.vue";
import MainQuestsView from "@/views/Quests/SubTabs/MainQuestsView.vue";
import { storeToRefs } from "pinia";
import { computed, markRaw, ref, watch } from "vue";
import { useRoute } from "vue-router";
import DailyQuestsView from "./SubTabs/DailyQuestsView.vue";
import SideQuestsView from "./SubTabs/SideQuestsView.vue";

export type ChronicleData = {
	id: number;
	quest?: QuestActive;
	heading: string;
};

const route = useRoute();

const questsEnabled = computed<boolean>(areStandardQuestsEnabled);

watch(
	(): readonly [boolean, boolean] => [
		isQuestAvailabilityReady(),
		questsEnabled.value,
	],
	([ready, enabled]: readonly [boolean, boolean]): void => {
		if (ready && !enabled) {
			void router.replace({
				name: RouteNames.LAYOUT_VIEW,
			});
		}
	},
	{ immediate: true },
);

const popupStore = usePopupStore();

const soundService = markRaw(SoundService.getInstance());
const questrepo = markRaw(QuestRepository.getInstance());

const questStore = useQuestStore();
const { tutorialStep, getActiveQuests, activeNarrativeId } =
	storeToRefs(questStore);

const settlementsStore = useSettlementsStore();
const { mainLevel } = storeToRefs(settlementsStore);

const isTutorialStep = ref<boolean>(false);
const isChroniclePopupOpen = ref<QuestTypes | null>(null);
const isStoryPlaying = ref<boolean>(false);
const forcePopupShow = ref<boolean>(false);

const isQuestPopupOpen = computed((): boolean => {
	if (!questsEnabled.value) return false;
	if (isChroniclePopupOpen.value) return false;
	return ((isTutorialStep.value && tutorialStep.value < 9) ||
		showPopup.value) &&
		!isStoryPlaying.value &&
		(activeTrackedQuest.value || newSideQuest.value)
		? true
		: false;
});

const numberOfClaimableDailyQuests = computed((): number => {
	const activeCompletedDailyQuests = getActiveQuests.value.filter(
		(q: QuestActive): boolean =>
			q.type === QuestTypes.DAILY &&
			q.progress >= getAmount(q.objective.amount),
	);
	return activeCompletedDailyQuests.length;
});
const numberOfClaimableSideQuests = computed((): number => {
	const activeCompletedSideQuests = getActiveQuests.value.filter(
		(q: QuestActive): boolean =>
			q.type === QuestTypes.SIDE &&
			q.progress >= getAmount(q.objective.amount),
	);
	return activeCompletedSideQuests.length;
});

const areDailyQuestsAvailable = computed((): boolean => {
	if (!questsEnabled.value) return false;
	return mainLevel.value >= 10;
});

const numberOfClaimableMainQuests = computed((): number => {
	const activeCompletedMainQuests = getActiveQuests.value.filter(
		(q: QuestActive): boolean =>
			q.type === QuestTypes.MAIN &&
			q.progress >= getAmount(q.objective.amount),
	);
	return activeCompletedMainQuests.length;
});

const settlementTabs = computed(() => {
	const tabs = [{ name: "ui.main_quests" }, { name: "ui.side_quests" }];
	if (areDailyQuestsAvailable.value) {
		tabs.push({ name: "ui.daily_quests" });
	}
	return tabs;
});

const shouldRenderQuestSubViews = computed((): boolean => {
	if (!questsEnabled.value) return false;
	return (
		!isQuestPopupOpen.value &&
		!isStoryPlaying.value &&
		!isChroniclePopupOpen.value
	);
});
const paramTabIndex = computed((): number => +route.params.tab || 0);

const showPopup = computed((): boolean => {
	return route.params.showPopup === "true" &&
		popupType.value === QuestPopupType.COMPLETED
		? true
		: forcePopupShow.value
			? true
			: false;
});

const handleQuestPopupClose = (type: QuestTypes = QuestTypes.MAIN): void => {
	forcePopupShow.value = false;
	if (type === QuestTypes.MAIN) {
		router.push({ name: RouteNames.QUESTS });
	}
};

const closeChroniclePopup = (): void => {
	isChroniclePopupOpen.value = null;
	forcePopupShow.value = false;
};

const activeTrackedQuest = computed((): QuestActive | null => {
	if (!questsEnabled.value) return null;
	if (route.params.questPopupId) {
		const trackedQuestId = +route.params.questPopupId;
		return (
			getActiveQuests.value.find(
				(q: QuestActive): boolean => q.id === trackedQuestId,
			) || null
		);
	}
	return null;
});

const newSideQuest = computed((): QuestActive | null => {
	if (!questsEnabled.value) return null;
	if (route.params.questPopupId) {
		const sideQuestId = +route.params.questPopupId;
		const sideQuest = quests.get(sideQuestId);
		if (!sideQuest || !isQuestTypeEnabled(sideQuest.type)) {
			return null;
		}
		return {
			id: sideQuestId,
			progress: 0,
			...sideQuest,
		} as QuestActive;
	}
	return null;
});

const questCompleted = computed((): boolean => {
	if (!activeTrackedQuest.value) {
		return false;
	}
	return (
		activeTrackedQuest.value.progress >=
		(typeof activeTrackedQuest.value.objective.amount === "function"
			? activeTrackedQuest.value.objective.amount()
			: activeTrackedQuest.value.objective.amount)
	);
});

const claimRewardButtonShow = computed((): boolean => {
	if (!activeTrackedQuest.value) {
		return false;
	}

	if (activeTrackedQuest.value?.payed === 0) {
		return false;
	} else if (activeTrackedQuest.value?.payed === 1) {
		return true;
	}

	return questCompleted.value;
});

const popupType = computed((): QuestPopupType => {
	return claimRewardButtonShow.value
		? QuestPopupType.COMPLETED
		: QuestPopupType.NEW;
});

const startChronicleStory = (id?: number, isStory?: boolean): void => {
	if (!questsEnabled.value) return;
	if (isStory) {
		startChapterStory(id ?? 1);
	}
	if (isStoryPlaying.value === true) return;
	isStoryPlaying.value = true;
};

const startChapterStory = (id: number): void => {
	if (!questsEnabled.value) return;
	const metaAction = questMetaActionsConfig[id];
	router.push({
		name: RouteNames.STORY,
		params: { chapter: metaAction?.params?.chapter ?? 1, id: "1" },
	});
};

const stopChronicleStory = (): void => {
	if (isStoryPlaying.value === false) return;
	isStoryPlaying.value = false;
};

const claimRewards = (id: number): void => {
	if (!questsEnabled.value) return;
	const claimableQuest = questsConfig.get(id);
	if (!claimableQuest) {
		return;
	}
	questrepo.addReward(id);
	questStore.finishQuest(id);
	soundService.playSettlementIncome();
	questrepo.finishSideQuest(id);

	if (claimableQuest?.type === QuestTypes.MAIN) {
		const nextQuest = questsConfig.get(id + 1);
		const metaAction = questMetaActionsConfig[id];
		if (
			metaAction &&
			metaAction.action === QuestMetaActions.START_CHAPTER_NARRATIVE
		) {
			router.push({
				name: RouteNames.STORY,
				params: { chapter: metaAction.params.chapter, id: "1" },
			});
		} else if (nextQuest) {
			questStore.addQuest(id + 1);
		}
	}
};

const mainQuests = computed((): Array<QuestActive> => {
	return [questStore.getFinishedQuests(), questStore.activeQuests]
		.flat()
		.filter((quest: QuestActive) => quest.type === QuestTypes.MAIN)
		.reverse();
});

const chapters = computed((): Array<{ heading: string; id: number }> => {
	const chapterHeadings: Array<{ heading: string; id: number }> = [];
	const maxIdMainQuest = Math.max(
		...mainQuests.value.map((quest: QuestActive) => quest.id),
		0,
	);
	for (const key of Object.keys(questMetaActionsConfig)) {
		const numKey = +key;
		if (numKey < maxIdMainQuest) {
			chapterHeadings.push({
				heading: translate("story.outro.chapter.heading").replace(
					"{number}",
					(
						questMetaActionsConfig[numKey].params.chapter - 1
					).toString(),
				),
				id: numKey,
			});
		}
	}
	return chapterHeadings.reverse();
});

const chroniclesData = computed((): Array<ChronicleData> => {
	if (isChroniclePopupOpen.value === QuestTypes.SIDE) {
		return [questStore.getFinishedQuests(), questStore.activeQuests]
			.flat()
			.filter((q: QuestActive) => q.type === QuestTypes.SIDE)
			.map((quest: QuestActive) => ({
				quest,
				heading: translate(quest.title),
				id: quest.id,
			}));
	}
	const dataToSort = [
		...chapters.value.map(
			(chapter: { heading: string; id: number }): ChronicleData => ({
				heading: chapter.heading,
				id: chapter.id,
			}),
		),
		...mainQuests.value.map(
			(quest: QuestActive): ChronicleData => ({
				quest,
				heading: quest.id + " " + translate(quest.title),
				id: quest.id,
			}),
		),
	];
	const chData = dataToSort.sort(
		(a: ChronicleData, b: ChronicleData): number => {
			if (b.id === a.id) return 1;
			return b.id - a.id;
		},
	);
	chData.push({ heading: translate("story.intro.heading"), id: 1 });
	return chData;
});

// 1. Track the visibility state of the main Quest popup and dispatch it to the Store
watch(
	(): QuestActive | null => {
		if (!questsEnabled.value) return null;
		const condition =
			(isQuestPopupOpen.value && activeTrackedQuest.value) ||
			newSideQuest.value;
		return condition
			? newSideQuest.value || activeTrackedQuest.value
			: null;
	},
	(
		questToShow: QuestActive | null,
		oldQuest: QuestActive | null | undefined,
	): void => {
		if (!questsEnabled.value) return;
		if (questToShow && !oldQuest) {
			popupStore.showPopup({
				id: `quest-${questToShow.id}`,
				type: PopupType.INFO,
				heading:
					popupType.value === QuestPopupType.COMPLETED
						? translate("ui.quest_completed")
						: translate("ui.new_quest"),
				maxHeight: 43,
				width: 24,
				isPaddingless: true,
				skin: SkinTypes.DARK,
				imgSrc: `quests/quest-${questToShow.type.toLowerCase()}-${popupType.value}.avif`,
				closeCallback: () => handleQuestPopupClose(questToShow.type),
				closeable:
					popupType.value === QuestPopupType.NEW
						? questToShow.type === QuestTypes.SIDE
						: true,
				component: markRaw(QuestPopup),
				componentProps: {
					quest: questToShow,
					popupType: popupType.value,
					onClaimRewards: claimRewards,
					onCloseCb: handleQuestPopupClose,
				},
			});
		}
	},
	{ immediate: true },
);

// 2. Dynamically update the Quest popup (e.g., when a quest is completed while the window is open)
watch(popupType, newType => {
	if (!questsEnabled.value) return;
	const questToShow = newSideQuest.value || activeTrackedQuest.value;
	if (questToShow && popupStore.isVisible) {
		popupStore.updatePopup({
			heading:
				newType === QuestPopupType.COMPLETED
					? translate("ui.quest_completed")
					: translate("ui.new_quest"),
			imgSrc: `quests/quest-${questToShow.type.toLowerCase()}-${newType}.avif`,
			componentProps: {
				quest: questToShow,
				popupType: newType,
				onClaimRewards: claimRewards,
				onCloseCb: handleQuestPopupClose,
			},
		});
	}
});

// 3. Handle opening the Chronicle popup
watch(isChroniclePopupOpen, (newVal, oldVal) => {
	if (!questsEnabled.value) return;
	if (newVal !== null && oldVal === null) {
		popupStore.showPopup({
			id: PopupDictionary.CHRONICLE,
			type: PopupType.INFO,
			heading: translate("button.chronicle"),
			height: 32.5,
			width: 24,
			skin: SkinTypes.DARK,
			isPaddingless: true,
			closeCallback: closeChroniclePopup, // Ensure the isChroniclePopupOpen variable is reset to null after closing
			component: markRaw(QuestChroniclePopup),
			componentProps: {
				chroniclesData: chroniclesData.value,
				onPlay: startChronicleStory,
			},
		});
	}
});

watch(
	(): number | null => activeNarrativeId.value,
	(
		newActiveNarrativeId: number | null,
		oldActiveNarrativeId?: number | null,
	): void => {
		if (newActiveNarrativeId !== null) {
			startChronicleStory();
		} else if (typeof oldActiveNarrativeId === "number") {
			stopChronicleStory();
			// force popup for narrative end
			forcePopupShow.value = true;
		} else {
			stopChronicleStory();
		}
	},
	{ immediate: true },
);
</script>

<style lang="scss" scoped>
.quests-view {
	&--chronicle-popup-open,
	&--story-playing,
	&--quest-popup-open {
		display: none;
	}
	:deep(.tabs__content) {
		max-height: 26rem;
	}
}

// required resize to make the entire panel content visible without scroll when a MAIN quest is active
:deep(.quests-view__main-modal--active .modal-window) {
	// IMPORTANT: Reset max height for the main quest modal window WITHOUT scrolling
	max-height: initial !important;
}

:deep(.quests-view__main-modal--active .scrollbar__scroller) {
	max-height: initial !important;
}

:deep(.quests-view__main-modal--active .main-quests) {
	max-height: initial !important;
}

.chronicle-button {
	margin-bottom: 0.5rem;
}
</style>
